import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import EvidenceGathering from "@/components/EvidenceGathering";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { DocumentUpdatePayload } from "@/hooks/useDocumentMetadata";
import { useDeleteProjectDocuments, useInsertProjectDocuments } from "@/hooks/useProjects";
import { findDocuments, recordDocumentEvents } from "@/lib/evidenceAudit";
import { Document } from "@/types/models";

interface EvidenceCatalogDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  documents: Document[];
  /** IDs already linked to the project — pre-checked in the grid and excluded from the "Add Selected" insert. */
  projectDocumentIds?: string[];
  isLoading?: boolean;
  isError?: boolean;
  error?: Error | null;
  onUpdateDocument: (
    payload: { id: string } & DocumentUpdatePayload,
    options?: { onSuccess?: () => void }
  ) => void;
  isUpdating?: boolean;
  projectId?: string;
  projectModel?: string;
}

const EvidenceCatalogDialog = ({
  open,
  onOpenChange,
  documents,
  projectDocumentIds,
  isLoading = false,
  isError = false,
  error = null,
  onUpdateDocument,
  isUpdating = false,
  projectId,
  projectModel,
}: EvidenceCatalogDialogProps) => {
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const insertProjectDocuments = useInsertProjectDocuments();
  const deleteProjectDocuments = useDeleteProjectDocuments();

  useEffect(() => {
    if (open) {
      setSelectedIds(projectDocumentIds ?? []);
    } else {
      setSelectedIds([]);
      insertProjectDocuments.reset();
      deleteProjectDocuments.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const linkedIds = projectDocumentIds ?? [];
  const toAdd = selectedIds.filter((id) => !linkedIds.includes(id));
  const toRemove = linkedIds.filter((id) => !selectedIds.includes(id));
  const hasChanges = toAdd.length > 0 || toRemove.length > 0;
  const isSaving = insertProjectDocuments.isPending || deleteProjectDocuments.isPending;

  const handleUpdateSelection = () => {
    if (!projectId || !hasChanges) return;

    const tasks: Promise<unknown>[] = [];
    if (toAdd.length > 0) {
      tasks.push(
        insertProjectDocuments
          .mutateAsync({ projectId, documentIds: toAdd })
          .then(() => recordDocumentEvents(projectId, "add_document", findDocuments(toAdd, documents)))
      );
    }
    if (toRemove.length > 0) {
      tasks.push(
        deleteProjectDocuments
          .mutateAsync({ projectId, documentIds: toRemove })
          .then(() => recordDocumentEvents(projectId, "remove_document", findDocuments(toRemove, documents)))
      );
    }

    Promise.all(tasks)
      .then(() => {
        setSelectedIds([]);
        onOpenChange(false);
      })
      .catch(() => {
        // Errors surface via insertProjectDocuments.isError / deleteProjectDocuments.isError below; keep the dialog open.
      });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[1320px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-lg font-bold">Select from Evidence Base</DialogTitle>
          <DialogDescription>Choose one or more documents to add to this project.</DialogDescription>
        </DialogHeader>

        {!isLoading && !isError && documents.length === 0 ? (
          <div className="py-12 text-center text-sm text-muted-foreground">
            No additional documents are available to add. Upload a new document instead.
          </div>
        ) : (
          <EvidenceGathering
            documents={documents}
            isLoading={isLoading}
            isError={isError}
            error={error}
            onUpdateDocument={onUpdateDocument}
            isUpdating={isUpdating}
            showCheckboxes
            showDocumentTabs
            showOwnershipFilter
            projectDocumentIds={projectDocumentIds}
            projectId={projectId}
            projectModel={projectModel}
            onSelectionChange={(_, ids) => setSelectedIds(ids)}
          />
        )}

        {(insertProjectDocuments.isError || deleteProjectDocuments.isError) && (
          <p className="text-sm text-destructive">
            Failed to update project documents: {(insertProjectDocuments.error ?? deleteProjectDocuments.error)?.message}
          </p>
        )}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="brand"
            disabled={!hasChanges || isSaving}
            onClick={handleUpdateSelection}
            className="gap-1.5"
          >
            {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            Update Project Documents
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EvidenceCatalogDialog;
